import React, {useContext, useEffect, useState} from "react";
import {Card, CardBody, Col, ListGroup, Row, Spinner} from "react-bootstrap";
import AppContext from '../contexts/app-context'
import * as backendUtils from "../helper/BackendUtils";
import Calendar from "react-calendar";
import 'react-calendar/dist/Calendar.css';
import './Home.css';
import {toast} from "react-toastify";
import Commute from "../components/Commute";
import {monthToName, olderThanXDays, simplifyDate, simplifyDateToMonth, weekends} from "../helper/DateUtils";
import {Tooltip} from 'react-tooltip'
import RideSummaryCard from "../components/RideSummaryCard";
import EventSummaryCard from "../components/EventSummaryCard";
import IconTip from "../components/IconTip";
import {Warehouse, Milestone, Building} from 'lucide-react';

export default function Home() {
    const context = useContext(AppContext);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const [date, setDate] = useState(new Date());
    const [month, setMonth] = useState(simplifyDateToMonth(new Date()));
    const [userId, setUserId] = useState("");
    const [total, setTotal] = useState({"home": 0, "moto": 0, "month": month, "distance": 0});
    const [participant, setParticipant] = useState(null);
    const [recentRides, setRecentRides] = useState([
        {}
    ]);
    const [joinedEvents, setJoinedEvents] = useState([]);
    const [showSpinner, setShowSpinner] = useState(true);

    const [eventStats, setEventStats] = useState([]);

    useEffect(() => {
        if (context.backendUri && userId) {
            backendUtils.getRequest(`${context.backendUri}/stats/participant/${userId}/recent`, {},
                response => {
                    const rides = response.data.map(r => {
                        return {
                            "title": r.name,
                            "date": r.date,
                            "distance": parseFloat(r.distance / 1000).toFixed(1) / 1.0,
                            "motoCommute": r.direction === 1 || r.direction === 2 ? "yes" : "no"
                        }
                    })
                    setRecentRides(rides.sort((a, b) => new Date(b.date) - new Date(a.date)))
                }, err => {
                    console.log(backendUtils.getErrorMessage(err));
                }
            ).then(() => setShowSpinner(false))
        }
    }, [context.backendUri, userId])

    useEffect(() => {
        const loadActiveEvents = () => {
            if (context.backendUri && userId) {
                backendUtils.getRequest(`${context.backendUri}/stats/participant/${userId}/eventCommutes`, {},
                    response => {
                        const now = new Date();
                        now.setUTCHours(0,0,0,0)
                        const active = response.data
                            .filter(e => now <= new Date(e.endDate))
                            .map(e => {
                                e.distance = parseFloat(e.distance / 1000).toFixed(1) / 1.0;
                                return e
                            })
                        setJoinedEvents(active.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)));
                    }, err => {
                        console.log(backendUtils.getErrorMessage(err));
                    }
                ).then(() => setShowSpinner(false))
                    .catch((err) => console.log(err))
            }
        }
        loadActiveEvents();
    }, [context.backendUri, userId])

    useEffect(() => {
        setUserId(context.userId)
    }, [context.userId])

    useEffect(() => {
        //TODO I'm not sure it works as expected
        if (error) {
            console.log(error)
            toast(error)
        }
    }, [error])

    useEffect(() => {
        const loadMonthCommutes = () => {
            setError(null)
            if (context.backendUri && userId) {
                backendUtils.getRequest(`${context.backendUri}/${userId}/month/${month}`, {},
                    response => {
                        setRecords(response.data);
                    }, err => {
                        setError(backendUtils.getErrorMessage(err));
                    }
                ).catch((err) => setError(err))
            }
        }
        loadMonthCommutes();
    }, [context.backendUri, userId, month])

    useEffect(() => {
        const calculateTotal = () => {
            if (records) {
                let totalHome = records.filter(commute => commute.direction === 1)
                    .reduce((acc, currentValue) => acc + 1, 0)
                let totalMoto = records.filter(commute => commute.direction === 2)
                    .reduce((acc, currentValue) => acc + 1, 0)
                let totalDistance = records.filter(commute => commute.isStrava === true)
                    .reduce((acc, currentValue) => acc + currentValue.distance, 0)
                setTotal({"home": totalHome, "moto": totalMoto, "month": month, "distance": totalDistance / 1000})
            }
        }
        calculateTotal()
    }, [records, month])

    useEffect(() => {
        const loadEventStats = () => {
            if (context.backendUri && userId && joinedEvents.length > 0) {
                const events = joinedEvents.map(event => event.id).join(',')
                backendUtils.getRequest(`${context.backendUri}/stats/participant/${userId}/eventStats?event=${events}`, {},
                    response => {
                        setEventStats(response.data)
                    }, err => {
                        console.log(backendUtils.getErrorMessage(err));
                    }
                ).then(() => setShowSpinner(false))
                    .catch((err) => console.log(err))
            }
        }
        loadEventStats();
    }, [context.backendUri, userId, joinedEvents])

    const handleSubmitted = (current) => {
        setError(null)
        let month = simplifyDateToMonth(current)
        if (context.backendUri && userId) {
            backendUtils.getRequest(`${context.backendUri}/${userId}/month/${month}`, {},
                response => {
                    setRecords(response.data);
                }, err => {
                    setError(backendUtils.getErrorMessage(err));
                }
            ).catch((err) => setError(err))
        }
    }

    const getParticipant = () => {
        if (context.backendUri && userId) {
            backendUtils.getRequest(`${context.backendUri}/participant/${userId}`, {},
                response => {
                    setParticipant(response.data);
                }, err => {
                    console.log(backendUtils.getErrorMessage(err));
                }
            ).then(() => setShowSpinner(false))
                .catch((err) => console.log(err))
        }
    }

    useEffect(() => {
        getParticipant()
    }, [userId, context.backendUri])

    const daysDisabledFromCompetition = ({activeStartDate, date, view}) => {
        return weekends(view, date) || olderThanXDays(new Date(), date, 7)
    }

    const handleDateChanged = (newDate) => {
        setDate(newDate)
        const newMonth = simplifyDateToMonth(newDate);
        if (newMonth !== month) setMonth(newMonth);
    }

    const monthChanged = ({action, activeStartDate, value, view}) => {
        const newMonth = simplifyDateToMonth(activeStartDate);
        if (newMonth !== month) setMonth(newMonth);
    }

    const show = ({date, view}) => {
        if (view === "month") {
            let finalScore = 0
            if (records && records.length > 0) {
                finalScore = records.filter(commute => commute.date === simplifyDate(date))
                    .map(commute => commute.isStrava ? commute.direction * 10 : commute.direction)
                    .reduce((total, value) => total + value, 0)
            }
            switch (finalScore) {
                case 1:
                    return <div><font size="1" color="blue">h</font></div>
                case 2:
                    return <div><font size="1" color="blue">m</font></div>
                case 3:
                    return <div><font size="1" color="blue">mh</font></div>
                case 10:
                    return <div><font size="1" color="orange">h</font></div>
                case 20:
                    return <div><font size="1" color="orange">m</font></div>
                case 30:
                    return <div><font size="1" color="orange">mh</font></div>
                case 12:
                    return <div><font size="1" color="orange">h</font><font size="1" color="blue">m</font></div>
                case 21:
                    return <div><font size="1" color="blue">h</font><font size="1" color="orange">m</font></div>
                default:
                    return <div>-</div>
            }
        }
    }

    const isValidParticipant = () => {
        return participant !== null;
    }

    return (
        <div>
            {
                showSpinner ? <div className="text-center m-2"><Spinner animation="border" variant="primary"/></div> :
                    <Row className="text-center">
                        <h1>Home</h1>
                        <hr/>
                        <Col>
                            {isValidParticipant() ? <Row>
                                <Col className="col">
                                    <hr className="m-3"/>
                                    <h3 className="text-center">Moto Calendar</h3>
                                    <hr className="m-3"/>
                                    <Calendar onChange={handleDateChanged} value={date}
                                              tileDisabled={daysDisabledFromCompetition}
                                              onActiveStartDateChange={monthChanged} tileContent={show}
                                              showNeighboringMonth={false}
                                              maxDate={new Date()} minDate={new Date(context.startFrom)}
                                    />
                                    <Card className="calendar-fit">
                                        <Card.Header className="text-center">Log your daily rides! <i
                                            className="bi bi-info-circle legend"/></Card.Header>
                                        <CardBody>
                                            <Commute user={userId} current={date} backendUri={context.backendUri}
                                                     handleError={(err) => setError(err)}
                                                     handleSubmitted={(current) => handleSubmitted(current)}/>
                                        </CardBody>
                                    </Card>
                                    <Tooltip style={{backgroundColor: "rgba(255, 255, 255, 0.29)", color: "#222"}}
                                             anchorSelect=".legend">
                                        <Card className="calendar-fit">
                                            <Card.Header className="calendar-fit">How to read calendar</Card.Header>
                                            <Card.Body>
                                                <ListGroup variant="flush">
                                                    <ListGroup.Item><b>m</b> commute to Motorola</ListGroup.Item>
                                                    <ListGroup.Item><b>h</b> commute home</ListGroup.Item>
                                                    <ListGroup.Item><font color="blue">provided
                                                        manually</font></ListGroup.Item>
                                                    <ListGroup.Item><font color="orange">uploaded by
                                                        Strava</font></ListGroup.Item>
                                                </ListGroup>
                                            </Card.Body>
                                        </Card>
                                    </Tooltip>
                                </Col>
                                <Col className="col">
                                    <div>
                                        <hr className="m-3"/>
                                        <h3 className="text-center">Monthly Recap</h3>
                                        <hr className="m-3"/>
                                        <Card className="calendar-fit">
                                            <Card.Header
                                                className="text-center">{monthToName(total.month)} activity</Card.Header>
                                            <Card.Body>
                                                <ListGroup horizontal
                                                           className="group flex items-center gap-2 text-xl-end">
                                                    <ListGroup.Item>
                                                        <IconTip tip="Rides to Motorola">
                                                            <Warehouse size={24}/>
                                                        </IconTip>
                                                        &nbsp;{total.moto}
                                                    </ListGroup.Item>
                                                    <ListGroup.Item>
                                                        <IconTip tip="Rides home">
                                                            <Building size={24}/>
                                                        </IconTip>
                                                        &nbsp;{total.home}
                                                    </ListGroup.Item>
                                                    <ListGroup.Item>
                                                        <IconTip tip="Distance tracked from Strava rides">
                                                            <Milestone size={24}
                                                                       className="me-2 text-success"/>
                                                        </IconTip>
                                                        &nbsp;{parseFloat(total.distance).toFixed(1)} km
                                                    </ListGroup.Item>
                                                </ListGroup>
                                            </Card.Body>
                                        </Card>
                                        <hr className="m-3"/>
                                        <h3 className="text-center">Your events</h3>
                                        <hr className="m-3"/>
                                        {
                                            joinedEvents.map((joinedEvent, index) => {
                                                const stats = eventStats.find(stat => stat.eventId === joinedEvent.id)
                                                    return (
                                                        <div key={index} className="mb-2">
                                                            <EventSummaryCard
                                                                eventName={joinedEvent.name}
                                                                {...joinedEvent}
                                                                {...stats}
                                                            />
                                                        </div>
                                                    );
                                                }
                                            )}
                                    </div>
                                </Col>
                                <hr className="m-3"/>
                                <Row className="justify-content-center">
                                    <h3 className="text-center">Recent rides</h3>
                                    <hr className="m-3"/>
                                    {recentRides.slice(0, 5)
                                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                                        .map((recentRide, index) =>
                                            <RideSummaryCard
                                                key={index}
                                                title={recentRide?.title}
                                                date={recentRide?.date}
                                                distance={recentRide?.distance === 0 ? "?" : recentRide?.distance}
                                                motoCommute={recentRide.motoCommute}
                                            />
                                        )}
                                </Row>
                            </Row> : <h1>Use 'Join Events' and join us!</h1>}
                        </Col>
                    </Row>
            }
            <hr className="hr m-3"/>
        </div>
    );
};